import React, { useState } from "react";
import {
  Mail, Inbox, Loader2, CheckCircle2, AlertCircle, Circle,
  UserRound, Handshake, MailOpen, Filter, UserPlus, X, Smartphone, Car,
  RotateCw, Link, User,
  ChevronRight, Pencil, Send, MailPlus, Activity, Package, Info,
} from "lucide-react";
import { Button } from "./ui-v2";
import {
  Badge, ProgressBar, Tabs, Tab, TabSeparator, ToggleGroup, ToggleItem,
  KpiCard, EmptyState, Switch,
  Tooltip, Input, Select, Textarea, Chips, ChipSelect, FileDrop, Pagination, StepIndicator,
  Table, Drawer, Modal,
} from "./ui-v2.5";
import { useNavbarOffset } from "./ui-v2.5/overlayBehavior";

/**
 * ShowcaseV25 — catálogo interno de ui-v2.5 (Fase 2.5).
 *
 * Documentación viva de desarrollo, NO pantalla de producto: no aparece en el menú,
 * se accede por URL (/ui-v2-5) y está gated a Super Administrador en App.js.
 * Fuente de verdad: ui-v2.5-catalogo-componentes.html.
 *
 * Tanda A poblada con todos los estados. Tandas B/C: placeholder hasta construirse.
 */

// —— Bloque contenedor tipo tarjeta del catálogo ——
function Demo({ title, children }) {
  return (
    <div className="mb-3 space-y-2">
      {title && <p className="text-xs font-medium text-slate-500">{title}</p>}
      {children}
    </div>
  );
}
function Panel({ children, className = "" }) {
  return <div className={`rounded-xl border border-slate-200 bg-white p-5 ${className}`}>{children}</div>;
}

// ============ DEMOS · TANDA A ============

function BadgeDemo() {
  return (
    <Panel>
      <Demo title="Tonos de estado (con icono)">
        <div className="flex flex-wrap gap-2">
          <Badge tone="waiting" icon={Mail}>Esperando datos</Badge>
          <Badge tone="received" icon={Inbox}>Datos recibidos</Badge>
          <Badge tone="progress" icon={Loader2}>En curso</Badge>
          <Badge tone="active" icon={CheckCircle2}>Completada</Badge>
          <Badge tone="expired" icon={AlertCircle}>Caducado</Badge>
          <Badge tone="info" icon={Circle}>Neutro</Badge>
        </div>
      </Demo>
      <Demo title="Sin icono">
        <div className="flex flex-wrap gap-2">
          <Badge tone="waiting">Borrador</Badge>
          <Badge tone="active">Activo</Badge>
          <Badge tone="info">Manual</Badge>
          <Badge tone="expired">Sin crear</Badge>
        </div>
      </Demo>
      <Demo title="Con punto en lugar de icono (tipo de entidad)">
        <div className="flex flex-wrap gap-2">
          <Badge tone="info" dot="#0ea5e9">Interno</Badge>
          <Badge tone="info" dot="#8b5cf6">Colaborador</Badge>
          <Badge tone="info" dot="#f59e0b">LAE Homes</Badge>
        </div>
      </Demo>
      <Demo title="Como contador dentro de otro componente">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="info">7</Badge>
          <Badge tone="received">3 nuevos</Badge>
        </div>
      </Demo>
    </Panel>
  );
}

function ProgressBarDemo() {
  return (
    <Panel>
      <Demo title="Umbral automático (0–40 warn · 41–75 brand · 76–100 ok)">
        <div className="max-w-md space-y-2.5">
          {[0, 20, 60, 85, 100].map((v) => (
            <ProgressBar key={v} value={v} label={`${v}%`} />
          ))}
        </div>
      </Demo>
      <Demo title="Sobre fondo oscuro (cabecera de Drawer)">
        <div className="max-w-md rounded-lg p-4" style={{ background: "#003c66" }}>
          <ProgressBar value={60} label="60% onboarding" onDark />
        </div>
      </Demo>
      <Demo title="Con recuento en lugar de porcentaje">
        <div className="max-w-md">
          <ProgressBar value={2} max={3} label="2/3 acusados" />
        </div>
      </Demo>
    </Panel>
  );
}

function TabsDemo() {
  const [a, setA] = useState("incorporaciones");
  const [b, setB] = useState("todas");
  return (
    <Panel>
      <Demo title="Estados: activa · inactiva · deshabilitada (title nativo, TODO Tanda B)">
        <Tabs value={a} onChange={setA}>
          <Tab value="incorporaciones" label="Incorporaciones" />
          <Tab value="bajas" label="Bajas" />
          <Tab value="perfiles" label="Perfiles" />
          <Tab value="documentos" label="Documentos" disabled tooltip="Disponible en próximas fases" />
          <Tab value="avisos" label="Avisos" disabled tooltip="Disponible en próximas fases" />
        </Tabs>
      </Demo>
      <Demo title="Con separador de grupo y contadores">
        <Tabs value={b} onChange={setB}>
          <Tab value="todas" label="Todas" count={7} />
          <Tab value="internos" label="Internos" count={5} />
          <TabSeparator />
          <Tab value="recursos" label="Recursos" />
          <Tab value="areas" label="Áreas" />
        </Tabs>
      </Demo>
    </Panel>
  );
}

function ToggleGroupDemo() {
  const [a, setA] = useState("int");
  const [b, setB] = useState("int");
  const [c, setC] = useState("int");
  return (
    <Panel>
      <Demo title="Con icono y contador">
        <ToggleGroup value={a} onChange={setA}>
          <ToggleItem value="int" label="Internos" icon={UserRound} count={4} />
          <ToggleItem value="col" label="Colaboradores" icon={Handshake} count={2} />
        </ToggleGroup>
      </Demo>
      <Demo title="Solo texto">
        <ToggleGroup value={b} onChange={setB}>
          <ToggleItem value="int" label="Internos" />
          <ToggleItem value="col" label="Colaboradores" />
        </ToggleGroup>
      </Demo>
      <Demo title="Con opción bloqueada por permiso (candado)">
        <ToggleGroup value={c} onChange={setC}>
          <ToggleItem value="int" label="Internos" />
          <ToggleItem value="col" label="Colaboradores" disabled />
        </ToggleGroup>
      </Demo>
    </Panel>
  );
}

function KpiCardDemo() {
  const [activo, setActivo] = useState(true);
  return (
    <Panel>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard icon={Loader2} label="En curso" value={3} />
        <KpiCard icon={Inbox} label="Datos recibidos" value={1} note="a completar" tone="received" />
        <KpiCard icon={MailOpen} label="Esperando datos" value={2} tone="waiting" />
        <KpiCard
          icon={Filter}
          label="Completadas"
          value={12}
          note={activo ? "filtro activo" : "pulsa para filtrar"}
          tone="active"
          active={activo}
          onClick={() => setActivo((v) => !v)}
        />
      </div>
    </Panel>
  );
}

function EmptyStateDemo() {
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-slate-200 bg-white">
        <EmptyState
          variant="full"
          icon={UserPlus}
          title="Sin incorporaciones registradas"
          description="Cuando solicites datos a un candidato o crees un alta manualmente, aparecerá aquí."
          action={<Button><UserPlus />Nueva incorporación</Button>}
        />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white">
        <EmptyState
          variant="medium"
          title="Sin resultados para los filtros aplicados."
          action={<Button variant="ghost" size="sm"><X />Limpiar filtros</Button>}
        />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white">
        <EmptyState variant="minimal" title="Sin bajas registradas." />
      </div>
    </div>
  );
}

function SwitchDemo() {
  const [s1, setS1] = useState(true);
  const [s2, setS2] = useState(false);
  const [r1, setR1] = useState(true);
  const [r2, setR2] = useState(false);
  return (
    <Panel>
      <Demo title="on · off · bloqueado on · bloqueado off">
        <div className="flex flex-wrap items-center gap-6">
          <Switch checked={s1} onChange={setS1} label="Activado" />
          <Switch checked={s2} onChange={setS2} label="Desactivado" />
          <Switch checked disabled label="Bloqueado on" />
          <Switch checked={false} disabled label="Bloqueado off" />
        </div>
      </Demo>
      <Demo title="En fila de lista (patrón de recursos y tareas)">
        <div className="max-w-md space-y-2">
          <label className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-slate-200 p-2.5">
            <Switch checked={r1} onChange={setR1} />
            <Smartphone className="h-4 w-4 text-slate-500" />
            <span className="flex-1 text-sm text-slate-700">Teléfono de empresa</span>
            <Badge tone="active">Inventario</Badge>
          </label>
          <label className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-slate-200 p-2.5">
            <Switch checked={r2} onChange={setR2} />
            <Car className="h-4 w-4 text-slate-500" />
            <span className="flex-1 text-sm text-slate-700">Coche</span>
          </label>
        </div>
      </Demo>
    </Panel>
  );
}

// ============ DEMOS · TANDA B ============

function TooltipDemo() {
  return (
    <Panel>
      <Demo title="Sobre botón DESHABILITADO (span envolvente) · normal · sobre icono">
        <div className="flex flex-wrap items-end gap-8">
          <div>
            <p className="mb-1 text-[11px] text-slate-400">Deshabilitado (arriba)</p>
            <Tooltip content="Disponible en próximas fases">
              <Button disabled><UserPlus />Nueva incorporación</Button>
            </Tooltip>
          </div>
          <div>
            <p className="mb-1 text-[11px] text-slate-400">Normal</p>
            <Tooltip content="Reenviar enlace al candidato">
              <Button variant="outline"><RotateCw />Reenviar</Button>
            </Tooltip>
          </div>
          <div>
            <p className="mb-1 text-[11px] text-slate-400">Sobre icono</p>
            <Tooltip content="Copiar enlace">
              <Button variant="ghost" size="icon"><Link /></Button>
            </Tooltip>
          </div>
        </div>
      </Demo>
      <Demo title="Cuatro posiciones (hover o foco con teclado)">
        <div className="flex flex-wrap gap-6 py-6">
          {["top", "bottom", "left", "right"].map((p) => (
            <Tooltip key={p} content={`Posición ${p}`} placement={p}>
              <Button variant="outline" size="sm">{p}</Button>
            </Tooltip>
          ))}
        </div>
      </Demo>
    </Panel>
  );
}

function InputDemo() {
  return (
    <Panel>
      <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
        <Input label="Nombre y apellidos *" placeholder="Ej: Lucía González" hint="Se usa para personalizar el email." />
        <Input label="Email personal *" icon={Mail} placeholder="candidato@email.com" />
        <Select label="Empresa / Negocio *">
          <optgroup label="Internos"><option>GRUPO LAE</option><option>LAE Homes</option></optgroup>
          <optgroup label="Colaboradores"><option>TSP</option></optgroup>
        </Select>
        <Input label="Fecha de incorporación" type="date" />
        <Input label="DNI / NIE *" defaultValue="1234" error="Formato no válido. 8 dígitos y una letra." />
        <Input label="Código de empleado" defaultValue="LH-0042" disabled hint="Lo asigna RRHH al crear el alta." />
        <Textarea label="Observaciones" rows={2} placeholder="Notas para Sistemas o RRHH…" wrapperClassName="sm:col-span-2" />
        <Input label="Prellenado del formulario" defaultValue="Ana García Martínez" prefilled hint="Dato aportado por la persona." />
        <Input label="IBAN" defaultValue="ES91 2100 0418 4502 0005 1332" mono />
      </div>
    </Panel>
  );
}

function ChipsDemo() {
  const [emails, setEmails] = useState([
    { label: "rrhh@grupolae.net", icon: User },
    { label: "i.lopetegi@grupolae.net", icon: User },
  ]);
  const [areas, setAreas] = useState([
    { label: "Sistemas", color: "#0ea5e9", meta: "2 pers." },
    { label: "RRHH", color: "#8b5cf6", meta: "2 pers." },
    { label: "Marketing", color: "#ec4899", meta: "1 pers." },
  ]);
  const [sel, setSel] = useState(["sis"]);
  return (
    <Panel>
      <Demo title="Dentro de un campo (entrada múltiple · Enter o coma añaden)">
        <label className="mb-1 block text-xs font-medium text-slate-600">Enviar aviso a *</label>
        <Chips value={emails} onChange={setEmails} input placeholder="Añadir otro email…" className="max-w-lg" />
        <p className="mt-1 text-[11px] text-slate-400">Enter o coma para añadir.</p>
      </Demo>
      <Demo title="Sueltos, con punto de color y contador (áreas)">
        <Chips value={areas} onChange={setAreas} />
      </Demo>
      <Demo title="Seleccionables (no eliminables)">
        <ChipSelect
          options={[
            { value: "sis", label: "Sistemas", color: "#0ea5e9" },
            { value: "adm", label: "Administración", color: "#ef4444" },
            { value: "ren", label: "Renting", color: "#f59e0b" },
          ]}
          value={sel}
          onChange={setSel}
        />
      </Demo>
    </Panel>
  );
}

function FileDropDemo() {
  const [f1, setF1] = useState(null);
  const [f2, setF2] = useState({ name: "titulo_grado_ade.pdf", size: 1.2 * 1024 * 1024 });
  const [sq, setSq] = useState({ name: "DNI_frente.jpg", size: 0.4 * 1024 * 1024 });
  const [sq2, setSq2] = useState(null);
  return (
    <Panel>
      <Demo title="Ancha · vacía (interactiva, máx. 10 MB) / con archivo / error">
        <div className="max-w-lg space-y-2">
          <FileDrop variant="wide" maxSize={10} accept="application/pdf,image/*" value={f1} onChange={setF1} hint="PDF, JPG o PNG · máx. 10 MB" />
          <FileDrop variant="wide" value={f2} onChange={setF2} />
          <FileDrop variant="wide" error="El archivo supera 10 MB" value={{ name: "escaneado_dni.tiff", size: 14.8 * 1024 * 1024 }} />
        </div>
      </Demo>
      <Demo title="Cuadrada · para imágenes (DNI)">
        <div className="flex flex-wrap gap-4">
          <div>
            <p className="mb-1.5 text-[11px] font-medium text-slate-500">Cara delantera</p>
            <FileDrop variant="square" value={sq} onChange={setSq} />
          </div>
          <div>
            <p className="mb-1.5 text-[11px] font-medium text-slate-500">Cara trasera</p>
            <FileDrop variant="square" maxSize={10} accept="image/*" value={sq2} onChange={setSq2} />
          </div>
        </div>
      </Demo>
    </Panel>
  );
}

function PaginationDemo() {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  return (
    <Panel>
      <div className="overflow-hidden rounded-v25-lg border border-v25-line">
        <div className="p-6 text-center text-xs text-slate-400">(cuerpo de tabla — 143 filas)</div>
        <Pagination
          page={page}
          pageSize={size}
          total={143}
          onPageChange={setPage}
          onPageSizeChange={(s) => { setSize(s); setPage(1); }}
        />
      </div>
      <p className="mt-2 text-[11px] text-slate-400">
        Página {page} · {size}/pág · total 143 → {Math.ceil(143 / size)} páginas. Elipsis a partir de 5.
      </p>
    </Panel>
  );
}

function StepIndicatorDemo() {
  return (
    <Panel>
      <Demo title="Puntos · con etiquetas (formulario público)">
        <StepIndicator variant="dots" steps={["Personales", "Dirección", "RRHH", "Formación", "Documentos"]} current={3} />
      </Demo>
      <Demo title="Barras (wizard en modal)">
        <StepIndicator variant="bars" steps={["Datos personales", "Dirección", "Empresa y puesto", "Material", "Avisos", "Revisión"]} current={3} />
      </Demo>
      <Demo title="Lista vertical (sidebar de wizard)">
        <StepIndicator variant="list" steps={["Datos personales", "Empresa y puesto", "Material y recursos"]} current={2} />
      </Demo>
    </Panel>
  );
}

// ============ DEMOS · TANDA C ============

function initials(name) {
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

function TableDemo() {
  const [sortBy, setSortBy] = useState("fecha");
  const [sortDir, setSortDir] = useState("desc");
  const [selected, setSelected] = useState(["ana"]);
  const [page, setPage] = useState(1);
  const rows = [
    { id: "ana", nombre: "Ana García Martínez", doc: "12345678A · ana.garcia@gmail.com", color: "#0ea5e9", tipo: "Interno", tcolor: "#0ea5e9", estado: "received", fecha: "1 jul 2026", prog: 20 },
    { id: "sara", nombre: "Sara Villanueva Cano", doc: "45612378A · sara.v@gmail.com", color: "#8b5cf6", tipo: "Colaborador", tcolor: "#8b5cf6", estado: "active", fecha: "12 jun 2026", prog: 100 },
    { id: "lucia", nombre: "Lucía González Ferrer", doc: "lucia.g@gmail.com", color: "#64748b", tipo: "Interno", tcolor: "#0ea5e9", estado: "waiting", fecha: "15 jul 2026", prog: 0 },
  ];
  const estadoBadge = {
    received: <Badge tone="received" icon={Inbox}>Datos recibidos</Badge>,
    active: <Badge tone="active" icon={CheckCircle2}>Completada</Badge>,
    waiting: <Badge tone="waiting" icon={Mail}>Esperando datos</Badge>,
  };
  const onSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(key); setSortDir("asc"); }
  };
  const columns = [
    {
      key: "nombre", header: "Persona", sortable: true,
      render: (r) => (
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-v25-full text-xs font-semibold text-white" style={{ background: r.color }}>{initials(r.nombre)}</span>
          <div><div className="font-medium text-v25-ink-900">{r.nombre}</div><div className="text-[11px] text-v25-ink-400">{r.doc}</div></div>
        </div>
      ),
    },
    { key: "tipo", header: "Tipo", render: (r) => <Badge tone="info" dot={r.tcolor}>{r.tipo}</Badge> },
    { key: "fecha", header: "Fecha alta", sortable: true, className: "whitespace-nowrap", render: (r) => r.fecha },
    { key: "estado", header: "Estado", render: (r) => estadoBadge[r.estado] },
    { key: "prog", header: "Progreso", render: (r) => <div style={{ width: 120 }}><ProgressBar value={r.prog} label={`${r.prog}%`} /></div> },
    {
      key: "acc", header: "", width: 44,
      render: () => (
        <Tooltip content="Ver detalle">
          <Button variant="ghost" size="icon"><ChevronRight /></Button>
        </Tooltip>
      ),
    },
  ];
  return (
    <div>
      <Table
        columns={columns}
        rows={rows}
        selectable
        selected={selected}
        onSelect={setSelected}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={onSort}
        rowHighlight={(r) => r.id === "ana"}
        footer={<Pagination page={page} pageSize={25} total={143} onPageChange={setPage} onPageSizeChange={() => setPage(1)} />}
      />
      <p className="mt-2 text-[11px] text-amber-700">
        ⚠ El icono de la última columna lleva un Tooltip "Ver detalle": sirve para comprobar el recorte por el overflow del contenedor (ver reporte de la tanda).
      </p>
    </div>
  );
}

function DrawerDemo() {
  const [brand, setBrand] = useState(false);
  const [plain, setPlain] = useState(false);
  const [stack, setStack] = useState(false);
  const [modalOnDrawer, setModalOnDrawer] = useState(false);
  return (
    <Panel>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => setBrand(true)}>Abrir Drawer · marca</Button>
        <Button variant="outline" onClick={() => setPlain(true)}><MailPlus />Abrir Drawer · formulario</Button>
        <Button variant="outline" onClick={() => setStack(true)}>Drawer + Modal apilados (probar Escape)</Button>
      </div>

      <Drawer
        open={brand}
        onClose={() => setBrand(false)}
        variant="brand"
        header={
          <div className="px-5 pb-4 pt-4 text-white" style={{ background: "#003c66" }}>
            <div className="mb-2 flex items-start justify-between">
              <button onClick={() => setBrand(false)} className="text-white/70 hover:text-white" aria-label="Cerrar" data-overlay-close="true"><X className="h-5 w-5" /></button>
              <button className="flex items-center gap-1 rounded-v25-md px-2.5 py-1.5 text-[11px] font-medium text-white" style={{ background: "rgba(255,255,255,.1)" }}><Pencil className="h-3.5 w-3.5" />Editar</button>
            </div>
            <div className="flex items-center gap-3">
              <span className="grid h-14 w-14 place-items-center rounded-v25-full text-lg font-semibold text-white" style={{ background: "#0ea5e9", boxShadow: "0 0 0 4px rgba(255,255,255,.15)" }}>AG</span>
              <div className="min-w-0"><h3 className="text-lg font-semibold leading-tight">Ana García</h3><div className="mt-1"><Badge tone="received">Datos recibidos</Badge></div></div>
            </div>
            <div className="mt-3"><ProgressBar value={20} label="20%" onDark /></div>
          </div>
        }
      >
        <div className="rounded-v25-lg border border-v25-line bg-white">
          <div className="flex items-center gap-2 border-b border-v25-surface-raised px-4 py-2.5 text-sm font-semibold"><Activity className="h-4 w-4 text-v25-brand-600" />Progreso</div>
          <div className="p-4 text-xs text-v25-ink-500">Contenido de sección…</div>
        </div>
        <div className="rounded-v25-lg border border-v25-line bg-white">
          <div className="flex items-center gap-2 border-b border-v25-surface-raised px-4 py-2.5 text-sm font-semibold"><Package className="h-4 w-4 text-v25-brand-600" />Material</div>
          <div className="p-4 text-xs text-v25-ink-500">Contenido de sección…</div>
        </div>
      </Drawer>

      <Drawer
        open={plain}
        onClose={() => setPlain(false)}
        variant="plain"
        header={
          <div className="flex items-start justify-between border-b border-v25-line px-5 py-4">
            <div><h3 className="flex items-center gap-2 text-sm font-semibold"><MailPlus className="h-4 w-4 text-v25-brand-600" />Solicitar datos</h3><p className="mt-0.5 text-[11px] text-v25-ink-500">Enviaremos un enlace seguro.</p></div>
            <button onClick={() => setPlain(false)} className="text-v25-ink-400 hover:text-v25-ink-700" aria-label="Cerrar" data-overlay-close="true"><X className="h-5 w-5" /></button>
          </div>
        }
        footer={
          <div className="flex gap-3 border-t border-v25-line bg-v25-surface-sunken px-5 py-4">
            <Button variant="outline" className="flex-1 justify-center" onClick={() => setPlain(false)}>Cancelar</Button>
            <Button className="flex-1 justify-center"><Send />Enviar</Button>
          </div>
        }
      >
        <div className="space-y-3 p-5">
          <Input label="Nombre *" placeholder="Ej: Lucía González" />
          <Input label="Email *" placeholder="candidato@email.com" />
          <Tooltip content="Este tooltip se ve por ENCIMA del Drawer">
            <Button variant="outline" size="sm"><Info />Botón con tooltip dentro del Drawer</Button>
          </Tooltip>
        </div>
      </Drawer>

      <Drawer
        open={stack}
        onClose={() => setStack(false)}
        variant="plain"
        header={<div className="border-b border-v25-line px-5 py-4"><h3 className="text-sm font-semibold">Drawer base</h3></div>}
      >
        <div className="space-y-3 p-5">
          <p className="text-sm text-v25-ink-500">Con este Drawer abierto, abre un Modal encima y pulsa Escape: debe cerrar primero el Modal y, otra vez, el Drawer.</p>
          <Button onClick={() => setModalOnDrawer(true)}>Abrir Modal encima</Button>
        </div>
      </Drawer>
      <Modal
        open={modalOnDrawer}
        onClose={() => setModalOnDrawer(false)}
        size="sm"
        title="Modal sobre Drawer"
        subtitle="Escape me cierra a mí primero"
        footer={<Button onClick={() => setModalOnDrawer(false)}>Entendido</Button>}
      >
        <p className="text-sm text-v25-ink-500">Escape cierra este Modal y el Drawer sigue abierto; Escape otra vez cierra el Drawer.</p>
      </Modal>
    </Panel>
  );
}

function ModalDemo() {
  const [size, setSize] = useState(null);
  const [danger, setDanger] = useState(false);
  return (
    <Panel>
      <div className="flex flex-wrap gap-2">
        {["sm", "md", "lg", "xl"].map((s) => (
          <Button key={s} variant="outline" onClick={() => setSize(s)}>Modal {s}</Button>
        ))}
        <Button variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => setDanger(true)}>Confirmación destructiva</Button>
      </div>

      <Modal
        open={!!size}
        onClose={() => setSize(null)}
        size={size || "md"}
        title={`Modal ${size || ""}`}
        subtitle="Cuerpo con scroll propio · max-height 92vh"
        footer={<><Button variant="ghost" onClick={() => setSize(null)}>Cancelar</Button><Button onClick={() => setSize(null)}>Continuar</Button></>}
      >
        <div className="space-y-2 text-sm text-v25-ink-500">
          {Array.from({ length: 14 }).map((_, i) => (
            <p key={i}>Línea de contenido {i + 1} — el cuerpo hace scroll propio si excede el 92% de la altura de la ventana.</p>
          ))}
        </div>
      </Modal>

      <Modal
        open={danger}
        onClose={() => setDanger(false)}
        size="sm"
        tone="danger"
        title="Cancelar la solicitud"
        footer={
          <>
            <Button variant="outline" className="flex-1 justify-center" onClick={() => setDanger(false)}>Volver</Button>
            <Button variant="destructive" className="flex-1 justify-center" onClick={() => setDanger(false)}>Cancelar solicitud</Button>
          </>
        }
      >
        El enlace enviado a Lucía González dejará de funcionar. Esta acción no se puede deshacer.
      </Modal>
    </Panel>
  );
}

// ============ Metadatos + registro de demos ============

const GROUPS = [
  {
    group: "Navegación",
    items: [
      { id: "tabs", name: "Tabs", tanda: "A", api: "<Tabs value onChange>\n  <Tab value label count? disabled? tooltip? />\n</Tabs>" },
      { id: "togglegroup", name: "ToggleGroup", tanda: "A", api: "<ToggleGroup value onChange>\n  <ToggleItem value label icon? count? disabled? />\n</ToggleGroup>" },
      { id: "stepindicator", name: "StepIndicator", tanda: "B", api: '<StepIndicator variant="dots|bars|list" steps current />' },
    ],
  },
  {
    group: "Datos",
    items: [
      { id: "table", name: "Table", tanda: "C", api: "<Table columns rows onSort sortBy sortDir\n       selectable selected onSelect rowHighlight />" },
      { id: "emptystate", name: "EmptyState", tanda: "A", api: '<EmptyState variant="full|medium|minimal"\n            icon title description action />' },
      { id: "pagination", name: "Pagination", tanda: "B", api: "<Pagination page pageSize total\n            onPageChange onPageSizeChange pageSizeOptions />" },
      { id: "kpicard", name: "KpiCard", tanda: "A", api: "<KpiCard icon label value note? tone? active? onClick? />" },
    ],
  },
  {
    group: "Estado",
    items: [
      { id: "badge", name: "Badge", tanda: "A", api: '<Badge tone="waiting|received|progress|active|expired|info"\n       icon? dot?>children</Badge>' },
      { id: "progressbar", name: "ProgressBar", tanda: "A", api: "<ProgressBar value max? label? tone? onDark? />" },
    ],
  },
  {
    group: "Formulario",
    items: [
      { id: "input", name: "Input · Select · Textarea", tanda: "B", api: "<Input label hint error icon prefilled disabled mono />\n<Select .../>   <Textarea .../>   // mismo envoltorio" },
      { id: "switch", name: "Switch", tanda: "A", api: "<Switch checked onChange disabled label? />" },
      { id: "chips", name: "Chips", tanda: "B", api: "<Chips value onChange removable />\n<ChipSelect options value onChange />" },
      { id: "filedrop", name: "FileDrop", tanda: "B", api: '<FileDrop variant="wide|square" accept maxSize\n          value onChange error />' },
    ],
  },
  {
    group: "Superposición",
    items: [
      { id: "tooltip", name: "Tooltip", tanda: "B", api: '<Tooltip content placement="top|bottom|left|right">\n  {children}\n</Tooltip>' },
      { id: "drawer", name: "Drawer", tanda: "C", api: '<Drawer open onClose variant="brand|plain" width? header footer>\n  {children}\n</Drawer>' },
      { id: "modal", name: "Modal", tanda: "C", api: '<Modal open onClose size title subtitle footer\n       tone="default|danger">{children}</Modal>' },
    ],
  },
];

const DEMOS = {
  badge: BadgeDemo,
  progressbar: ProgressBarDemo,
  tabs: TabsDemo,
  togglegroup: ToggleGroupDemo,
  kpicard: KpiCardDemo,
  emptystate: EmptyStateDemo,
  switch: SwitchDemo,
  tooltip: TooltipDemo,
  input: InputDemo,
  chips: ChipsDemo,
  filedrop: FileDropDemo,
  pagination: PaginationDemo,
  stepindicator: StepIndicatorDemo,
  table: TableDemo,
  drawer: DrawerDemo,
  modal: ModalDemo,
};

const TANDA_PILL = {
  A: "bg-amber-50 text-amber-700 border-amber-200",
  B: "bg-violet-50 text-violet-700 border-violet-200",
  C: "bg-rose-50 text-rose-700 border-rose-200",
};

function Section({ item, offset }) {
  const DemoComp = DEMOS[item.id];
  return (
    <section id={item.id} style={{ scrollMarginTop: (offset || 0) + 24 }}>
      <div className="mb-3 flex items-center gap-3">
        <h2 className="text-lg font-semibold text-slate-900">{item.name}</h2>
        {DemoComp ? (
          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700">
            Listo · Tanda {item.tanda}
          </span>
        ) : (
          <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${TANDA_PILL[item.tanda]}`}>
            Pendiente · Tanda {item.tanda}
          </span>
        )}
      </div>

      {DemoComp ? (
        <DemoComp />
      ) : (
        <div className="mb-3 rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
          <p className="text-sm text-slate-400">
            Demostración pendiente · se poblará en la Tanda {item.tanda} con todos los estados del catálogo.
          </p>
        </div>
      )}

      <div>
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">API</p>
        <pre className="overflow-x-auto whitespace-pre rounded-md bg-slate-900 p-3 font-mono text-xs leading-relaxed text-sky-200">{item.api}</pre>
      </div>
    </section>
  );
}

export default function ShowcaseV25() {
  const total = GROUPS.reduce((n, g) => n + g.items.length, 0);
  const listos = GROUPS.reduce((n, g) => n + g.items.filter((it) => DEMOS[it.id]).length, 0);

  // Mismo hook que Drawer/Modal: la cabecera sticky del catálogo se pega JUSTO debajo del NavBar.
  const navH = useNavbarOffset();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="sticky z-30 border-b border-slate-200 bg-white/95 backdrop-blur" style={{ top: navH }}>
        <div className="mx-auto flex max-w-[1400px] items-center gap-4 px-6 py-3">
          <div className="grid h-8 w-8 place-items-center rounded-lg text-xs font-bold text-white" style={{ background: "#003c66" }}>
            v2.5
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-semibold leading-tight">ui-v2.5 · Catálogo de componentes</h1>
            <p className="text-xs text-slate-500">
              Documentación de desarrollo · {listos}/{total} listos · solo Super Administrador · fuera del menú
            </p>
          </div>
          <span className="hidden rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700 md:inline">
            Fase 2.5 · Tanda A
          </span>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1400px] gap-8 px-6 py-6">
        <nav className="hidden w-52 shrink-0 lg:block">
          <div className="sticky space-y-4" style={{ top: navH + 16 }}>
            {GROUPS.map((g) => (
              <div key={g.group}>
                <p className="mb-1 px-2.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{g.group}</p>
                {g.items.map((it) => (
                  <a
                    key={it.id}
                    href={`#${it.id}`}
                    className="flex items-center justify-between rounded-md px-2.5 py-1 text-[13px] text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  >
                    <span>{it.name}</span>
                    <span className={DEMOS[it.id] ? "text-[10px] text-emerald-500" : "text-[10px] text-slate-400"}>{it.tanda}</span>
                  </a>
                ))}
              </div>
            ))}
          </div>
        </nav>

        <main className="min-w-0 flex-1 space-y-10">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="mb-2 text-sm font-semibold">Cómo usar este catálogo</h2>
            <p className="text-sm leading-relaxed text-slate-600">
              Cada sección corresponde a uno de los {total} componentes de ui-v2.5, en el orden del catálogo de
              especificación. Los marcados <span className="font-medium text-emerald-600">Listo</span> están construidos
              con todos sus estados; los <span className="font-medium text-slate-500">Pendiente</span> se poblarán en su
              tanda. La fuente de verdad es <code>ui-v2.5-catalogo-componentes.html</code>.
            </p>
          </div>

          {GROUPS.map((g) => (
            <div key={g.group} className="space-y-8">
              <p className="border-b border-slate-200 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                {g.group}
              </p>
              {g.items.map((it) => (
                <Section key={it.id} item={it} offset={navH} />
              ))}
            </div>
          ))}

          <div className="h-10" />
        </main>
      </div>
    </div>
  );
}
